import { useEffect, useRef, useState } from 'react'
import { useAccount, useChainId, useSimulateContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi'

import useCSTClaimable from './useCSTClaimable'

export default function useWrite({
  contract,
  abi,
  functionName,
  args,
  loading,
  enabled,
  onTransactionComplete,
  isMint,
  isBurn,
  waitForReceipt,
}: any) {
  const chainId = useChainId()

  const account = useAccount()

  const claimable = useCSTClaimable(false)
  const [claim, setClaim] = useState(0)
  const [writeProcessed, setWriteProcessed] = useState(false) // Track if the write has already been processed

  enabled = enabled && account.address !== undefined

  const writeData = {
    chainId,
    query: {
      enabled,
    },
    address: contract as `0x${string}`,
    abi,
    functionName,
    args,
    account: account.address,
  }

  const { data, error } = useSimulateContract(writeData)

  if (error) {
    // This is just a prewrite error, we can ignore it
    // eslint-disable-next-line no-console
    console.error('Error in useWrite', error)
  }

  const callbackCalled = useRef(false)
  const contractWrite = useWriteContract()
  const confirmation = useWaitForTransactionReceipt({
    hash: contractWrite.data,
  })

  // useEffect to handle completion of the transaction
  useEffect(() => {
    if (confirmation.isSuccess && onTransactionComplete && !callbackCalled.current) {
      if (isMint) {
        onTransactionComplete(claim)
        setClaim(0)
      } else if (isBurn) {
        onTransactionComplete(null)
      } else if (waitForReceipt) {
        onTransactionComplete(contractWrite.data)
      } else {
        onTransactionComplete()
      }
      callbackCalled.current = true
    }

    if (contractWrite.isPending) {
      callbackCalled.current = false
    }
  }, [confirmation.isSuccess, onTransactionComplete])

  // useEffect to track when contractWrite data becomes non-null
  useEffect(() => {
    if (onTransactionComplete) {
      if (contractWrite && contractWrite.data && !writeProcessed) {
        setWriteProcessed(true) // Ensure this is only processed once
        if (isBurn && !waitForReceipt) {
          onTransactionComplete(contractWrite.data)
        }
      }
    }
  }, [contractWrite, onTransactionComplete])

  if (contractWrite.isError) {
    if (
      contractWrite.error.message.includes('User denied transaction signature') ||
      contractWrite.error.message.includes('User rejected the request')
    ) {
      contractWrite.reset()
      // eslint-disable-next-line no-console
      console.log('User denied transaction signature')
      return {
        state: 'enabled',
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('Error in contractWrite', contractWrite.error)
      return {
        state: 'error',
      }
    }
  }

  if (!enabled) {
    return {
      state: 'disabled',
    }
  }

  if (loading || contractWrite.isPending || confirmation.isLoading) {
    return {
      state: 'loading',
    }
  }

  if (contractWrite.writeContract === undefined) {
    // eslint-disable-next-line no-console
    console.log('contractWrite error', contractWrite)
    return {
      state: 'disabled',
    }
  }

  return {
    state: 'enabled',
    write: async () => {
      if (data === undefined) {
        // eslint-disable-next-line no-console
        console.error('Data is undefined')
        contractWrite.reset()
      }
      if (isMint) {
        setClaim(Number(claimable.value) / 10 ** 6)
      }
      try {
        contractWrite.writeContract(data?.request)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error in contractWrite', contractWrite)
      }
    },
    hash: contractWrite.data,
    reload: contractWrite.reset,
  }
}

/*export default function useWrite({
  contract,
  abi,
  functionName,
  args,
  loading,
  enabled,
  onTransactionComplete,
  isMint,
}: any) {
  const chainId = useChainId()
  const account = useAccount()
  const claimable = useCSTClaimable(false)
  const [claim, setClaim] = useState(0)

  enabled = enabled && account.address !== undefined

  const { data, error } = useSimulateContract({
    chainId,
    query: {
      enabled,
    },
    address: contract,
    abi,
    functionName,
    args,
  })

  if (error) {
    // This is just a prewrite error, we can ignore it
    // eslint-disable-next-line no-console
    console.error('Error in useWrite', error)
  }
  const callbackCalled = useRef(false)

  const contractWrite = useWriteContract()

  const confirmation = useWaitForTransactionReceipt({
    hash: contractWrite.data,
  })

  // useEffect to handle completion of the transaction
  useEffect(() => {
    // Check if the transaction has been confirmed
    if (confirmation.isSuccess && onTransactionComplete && !callbackCalled.current) {
      // Run the callback function
      if (isMint) {
        onTransactionComplete(claim)
        setClaim(0)
      } else {
        onTransactionComplete()
      }
      callbackCalled.current = true
    }
    if (contractWrite.isPending) {
      callbackCalled.current = false
    }
  }, [confirmation.isSuccess, onTransactionComplete])

  if (contractWrite.isError) {
    // Handle errors
    if (
      contractWrite.error.message.includes('User denied transaction signature') ||
      contractWrite.error.message.includes('User rejected the request')
    ) {
      contractWrite.reset()
      // eslint-disable-next-line no-console
      console.log('User denied transaction signature')
      return {
        state: 'enabled',
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('Error in contractWrite', contractWrite.error)
      return {
        state: 'error',
      }
    }
  }

  if (!enabled) {
    return {
      state: 'disabled',
    }
  }

  if (loading || contractWrite.isPending || confirmation.isLoading) {
    //BURN HASH HERE
    console.log("UseWrite Loading: ", contractWrite)
    return {
      state: 'loading',
    }
  }

  if (contractWrite.writeContract === undefined) {
    // eslint-disable-next-line no-console
    console.log('contractWrite error', contractWrite)

    return {
      state: 'disabled',
    }
  }

  return {
    state: 'enabled',
    write: async () => {
      if (data === undefined) {
        // eslint-disable-next-line no-console
        console.error('Data is undefined')
        contractWrite.reset()
      }
      if (isMint) {
        setClaim(Number(claimable.value) / 10 ** 6)
      }
      try {
        contractWrite.writeContract(data?.request)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error in contractWrite', contractWrite)
      }
    },
    hash: contractWrite.data,
    reload: contractWrite.reset,
  }
}*/
